import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "../../components/ui/modal.tsx";
import { MessagePair } from "./messagePair.tsx";
import { IssaChatAvatar } from "./issaChatAvatar.tsx";
import { issaActivity } from "./issaActivity.ts";
import { IssaJumpToLatest, IssaWorkingStatus } from "./issaWorkingStatus.tsx";
import { deleteConversation, generateConversationGuid, type LogResponse } from "./issaInsightsApi.ts";
import { useIssaPrompts } from "./issaPromptContext.tsx";
import { extractNextPrompt } from "./issaResponseHelpers.ts";
import { isAnalystEndpoint } from "../../auth/endpointUtils.ts";
import { getDbFeatureFlags } from "../../util/dbFeatureFlags.ts";
import { useAuth } from "../../auth/authContext.tsx";
import { useAlertDialog } from "../../hooks/useAlertDialog.tsx";
import { useScrollFollow } from "../../hooks/useScrollFollow.ts";
import { useBrand } from "../../config/useBrand.ts";
import issaStyles from "./issaInsights.module.scss";
import qcStyles from "./issaQuickChat.module.scss";

interface IssaQuickChatProps {
  open: boolean;
  prompt: string;
  displayPrompt?: string;
  model?: string;
  onClose: () => void;
}

export function IssaQuickChat({ open, prompt, displayPrompt, model, onClose }: IssaQuickChatProps) {
  const navigate = useNavigate();
  const { activeEndpoint, cache } = useAuth();
  const { choose } = useAlertDialog();
  const { assistant, title: brandTitle } = useBrand();
  // New quick-chat conversations use the endpoint-appropriate model when the
  // caller doesn't specify one ("auto" is retired).
  const newChatModel = assistant.modelConfigId
    ?? (isAnalystEndpoint(activeEndpoint, getDbFeatureFlags(cache).IssaGroupDataset !== false) ? "default" : "assist");
  const { tasks, startPrompt, cancelPrompt, notifyWhenReady, removeTask } = useIssaPrompts();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [log, setLog] = useState<LogResponse | null>(null);
  const [conversationGuid, setConversationGuid] = useState<string | null>(null);
  const [followUp, setFollowUp] = useState("");
  const [statusText, setStatusText] = useState<string | null>(null);
  const [partialText, setPartialText] = useState<string | null>(null);
  const [startedAt, setStartedAt] = useState<number | undefined>(undefined);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [showNotifyAction, setShowNotifyAction] = useState(false);
  const startedKeyRef = useRef<string | null>(null);
  const closePromptOpenRef = useRef(false);
  const task = tasks.find((item) => item.id === taskId);
  const {
    ref: chatRef,
    away: awayFromLatest,
    unseen: unseenBelow,
    landed: answerLanded,
    toEnd: scrollToLatest,
  } = useScrollFollow();

  // A prompt starts at the end, so a long prepared one (a chart's rows, say)
  // never leaves the avatar and its thinking line below the fold.
  useEffect(() => {
    if (taskId) scrollToLatest();
  }, [taskId, scrollToLatest]);

  useEffect(() => {
    if (!task || task.status !== "pending" || task.detached) {
      setShowNotifyAction(false);
      return;
    }
    const remaining = Math.max(0, 5000 - (Date.now() - task.startedAt));
    if (remaining === 0) {
      setShowNotifyAction(true);
      return;
    }
    setShowNotifyAction(false);
    const timer = window.setTimeout(() => setShowNotifyAction(true), remaining);
    return () => window.clearTimeout(timer);
  }, [task]);

  // Auto-submit the prompt when the modal opens
  useEffect(() => {
    if (!open || !prompt) return;

    setLoading(true);
    setError(null);
    setLog(null);
    setFollowUp("");

    const startKey = `${prompt}|${displayPrompt ?? ""}|${model ?? newChatModel}`;
    if (startedKeyRef.current === startKey) return;
    startedKeyRef.current = startKey;

    const guid = generateConversationGuid();
    setConversationGuid(guid);
    setTaskId(startPrompt({
      conversationGuid: guid,
      prompt,
      displayPrompt,
      model: model ?? newChatModel,
      origin: "quick-chat",
    }));
  }, [open, prompt, displayPrompt, model, newChatModel, startPrompt]);

  useEffect(() => {
    if (!task) return;
    setStatusText(task.statusText ?? null);
    setPartialText(task.partialText ?? null);
    setStartedAt(task.startedAt);
    if (task.detached && task.status === "pending") {
      setLoading(false);
      onClose();
      return;
    }
    if (task.status === "completed") {
      setConversationGuid(task.conversationGuid);
      const last = task.logs?.[task.logs.length - 1];
      if (last) setLog(last);
      answerLanded();
      setLoading(false);
    } else if (task.status === "error") {
      setError(task.error ?? "Failed to get response");
      setLoading(false);
    } else if (task.status === "cancelled") {
      setLoading(false);
    }
  }, [answerLanded, onClose, task]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setLog(null);
      setConversationGuid(null);
      setLoading(false);
      setError(null);
      setFollowUp("");
      setStatusText(null);
      setTaskId(null);
      startedKeyRef.current = null;
    }
  }, [open]);

  const cancelQuickChat = useCallback(async (taskId: string) => {
    const taskToCancel = tasks.find((item) => item.id === taskId);
    if (!taskToCancel) return;
    cancelPrompt(taskId);
    if (taskToCancel.conversationWasEmpty) {
      try { await deleteConversation(taskToCancel.conversationGuid); } catch { /* may not exist yet */ }
    }
    removeTask(taskId);
    onClose();
  }, [cancelPrompt, onClose, removeTask, tasks]);

  const handleClose = useCallback(async () => {
    if (task?.status === "pending" && !task.detached) {
      const hasWaitedFiveSeconds = Date.now() - task.startedAt >= 5000;
      if (hasWaitedFiveSeconds) {
        if (closePromptOpenRef.current) return;
        closePromptOpenRef.current = true;
        const choice = await choose<"notify" | "cancel">({
          title: `${assistant.name} is still working`,
          body: `Would you like ${assistant.name} to notify you when the answer is ready? You can continue using ${brandTitle} while it finishes.`,
          cancelLabel: "Keep waiting",
          options: [
            { label: "Cancel request", value: "cancel", variant: "danger" },
            { label: "Notify me", value: "notify", variant: "primary" },
          ],
        });
        closePromptOpenRef.current = false;
        if (choice === null) return;
        if (choice === "notify") {
          notifyWhenReady(task.id);
        } else {
          await cancelQuickChat(task.id);
          return;
        }
        onClose();
        return;
      }
      await cancelQuickChat(task.id);
      return;
    }
    onClose();
  }, [assistant.name, brandTitle, cancelQuickChat, choose, notifyWhenReady, onClose, task]);

  const handleNotify = useCallback(() => {
    if (!task || task.status !== "pending") return;
    notifyWhenReady(task.id);
    onClose();
  }, [notifyWhenReady, onClose, task]);

  // The answer's suggested follow-up (<next>): "Yes" continues in the full chat
  // with it, and Tab fills it into the follow-up box.
  const nextSuggestion = log && !loading ? extractNextPrompt(log.Response) : null;

  const handleContinue = useCallback((text?: string) => {
    if (!conversationGuid) return;
    const trimmed = (text ?? followUp).trim();
    handleClose();
    navigate("/page/issainsights", {
      state: {
        conversationGuid,
        followUpPrompt: trimmed || undefined,
        model: model ?? newChatModel,
      },
    });
  }, [conversationGuid, followUp, model, newChatModel, handleClose, navigate]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleContinue();
        return;
      }
      if (e.key === "Tab" && !e.shiftKey && nextSuggestion && followUp.length < nextSuggestion.length
          && nextSuggestion.toLowerCase().startsWith(followUp.toLowerCase())) {
        e.preventDefault();
        setFollowUp(nextSuggestion);
      }
    },
    [followUp, handleContinue, nextSuggestion],
  );

  return (
    <Modal open={open} onClose={handleClose} wide>
      <ModalHeader title={`${assistant.name} Analysis`} onClose={handleClose} />
      <ModalBody>
        <div className={qcStyles.chatFrame}>
          <div className={qcStyles.chatBody} ref={chatRef}>
            {loading && (
              <>
                <div className={issaStyles.messageUser}>
                  <div>
                    <div className={issaStyles.bubbleUser}>{displayPrompt ?? prompt}</div>
                  </div>
                </div>
                <div className={issaStyles.messageAi}>
                  <IssaChatAvatar show state={partialText?.trim() ? "responding" : "thinking"} activity={issaActivity(statusText)} effects />
                  <div className={`${issaStyles.bubbleAi} ${issaStyles.typingBubble}`}>
                    <IssaWorkingStatus statusText={statusText} fallback="Thinking…" partialText={partialText} startedAt={startedAt} />
                  </div>
                </div>
              </>
            )}
            {log && !loading && (
              <MessagePair
                log={log}
                onUseNext={nextSuggestion ? (text) => handleContinue(text) : undefined}
              />
            )}
            {error && <div className={qcStyles.errorMsg} role="alert">{error}</div>}
          </div>
          {awayFromLatest && (loading || unseenBelow) && (
            <IssaJumpToLatest
              answering={loading}
              assistantName={assistant.name}
              onClick={() => scrollToLatest(true)}
            />
          )}
        </div>
        {/* Follow-up input — visible once response is loaded */}
        {log && !loading && (
          <div className={qcStyles.followUpArea}>
            <textarea
              className={qcStyles.followUpInput}
              aria-label="Follow-up question"
              name="followUp"
              autoComplete="off"
              placeholder={nextSuggestion ? `${nextSuggestion} (Tab to use)` : "Ask a follow-up question…"}
              rows={1}
              value={followUp}
              onChange={(e) => setFollowUp(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button className={qcStyles.continueBtn} onClick={() => handleContinue()}>
              Continue in {assistant.name} →
            </button>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <button className={qcStyles.closeBtn} onClick={handleClose}>{loading ? "Cancel" : "Close"}</button>
        {showNotifyAction && (
          <button className={qcStyles.notifyBtn} onClick={handleNotify}>Notify me</button>
        )}
      </ModalFooter>
    </Modal>
  );
}
